import { all, call, put, takeEvery, select } from 'redux-saga/effects';
import { fetchEntity, fetchEntityIfNeeded } from '@classpass/saga-tools';
import _includes from 'lodash/includes';
import { practitioners, resources, services, msInventoryVenues } from 'store/saga/entities/bound';
import { NEW_SERVICE_BANNER_LOCAL_STORAGE_KEY, VENUE_SERVICE_TYPES } from 'constants/logic';
import { setKeyForUser } from 'helpers/local-storage';
import { makePath } from 'helpers/link-generation';
import {
  selectActiveVenue,
  selectRouteParams,
  selectVenueByVenueId,
  selectUserVenues,
  selectUserId,
} from 'selectors/general';
import { createErrorBanner } from 'components/Banner';
import { ACTIVE_VENUE_CHANGED, waitForAppInit } from 'features/App/App.saga';
import { reportError } from 'helpers/error-logging';
import { handleClassesRouteEnter } from './ClassList';
import { selectTabFirstItemId } from './ManageServices.selectors';
import messages from './ManageServices.messages';

export const GO_TO_TAB = 'manageServices/GO_TO_TAB';
export const goToTab = tab => ({ type: GO_TO_TAB, tab });

export const GO_TO_ADD = 'manageServices/GO_TO_ADD';
export const goToAdd = () => ({ type: GO_TO_ADD });

export const GO_TO_EDIT = 'manageServices/GO_TO_EDIT';
export const goToEdit = () => ({ type: GO_TO_EDIT });

export const GO_TO_ITEM = 'manageServices/GO_TO_ITEM';
export const goToItem = itemId => ({ type: GO_TO_ITEM, itemId });

export const HIDE_SERVICE_BANNER = 'manageServices/HIDE_SERVICE_BANNER';
export const hideServiceBanner = () => ({ type: HIDE_SERVICE_BANNER });

export function* doGoToTab(client, action) {
  const { tab: nextTab } = action;
  const { tab: currentTab, venueId } = yield select(selectRouteParams);
  const itemId = yield select(selectTabFirstItemId, nextTab || currentTab);
  const newPath = makePath.manageServices({
    venueId,
    tab: nextTab || currentTab,
    itemId,
  });
  yield call(client.navigate, newPath);
}

export function* doGoToAdd(client) {
  const routeParams = yield select(selectRouteParams);
  const newPath = makePath.manageServicesAdd({
    ...routeParams,
    focus: 'details',
  });
  yield call(client.navigate, newPath);
}

export function* doGoToEdit(client) {
  const routeParams = yield select(selectRouteParams);
  const newPath = makePath.manageServicesEdit({
    ...routeParams,
    focus: 'details',
  });
  yield call(client.navigate, newPath);
}

export function* doGoToItem(client, action) {
  const { itemId } = action;
  const routeParams = yield select(selectRouteParams);
  const newPath = makePath.manageServices({
    ...routeParams,
    itemId,
    focus: 'details',
  });
  yield call(client.navigate, newPath);
}

export function* handleRouteEnter(client) {
  try {
    yield call(waitForAppInit);

    const routeParams = yield select(selectRouteParams);
    const { tab } = routeParams;
    const isAddRoute = client.location.pathname?.includes('add');
    const venue = yield select(selectActiveVenue);
    const { isMsInventoryVenue } = venue;
    const venuesList = yield select(selectUserVenues);

    let { venueId, itemId } = routeParams;
    if (!venueId) {
      ({ venueId } = venue);
    }

    const userHasManageServicesEligibleVenue = venuesList.some(v =>
      _includes(v.serviceTypes, VENUE_SERVICE_TYPES.APPOINTMENT)
    );

    if (!userHasManageServicesEligibleVenue) {
      const newPath = makePath.home(venueId);
      yield call(client.navigate, newPath);
    }

    if (isMsInventoryVenue) {
      yield all([
        call(fetchEntity, practitioners, [client, venueId]),
        call(fetchEntity, resources, [client, venueId]),
        call(fetchEntity, services, [client, venueId]),
        call(fetchEntityIfNeeded, msInventoryVenues, [client, venueId]),
      ]);

      if (!isAddRoute && !itemId) {
        itemId = yield select(selectTabFirstItemId, tab);
      }
    } else {
      yield call(handleClassesRouteEnter, client, { venueId });
    }

    if (
      !isAddRoute &&
      ((!!venueId && routeParams.venueId !== venueId) || (!!itemId && routeParams.itemId !== itemId)) // !!item prevents `undefined != null`
    ) {
      const newPath = makePath.manageServices({ venueId, tab, itemId });
      yield call(client.navigate, newPath);
    }
  } catch (error) {
    yield all([put(createErrorBanner(messages.error.problemWithRequest)), call(reportError, error)]);
  }
}

export function* handleActiveVenueChange(client, action) {
  const { venueId } = action;

  // Replicating some handleRouteEnter logic here because we don't want to use locationSagas
  // on the route because changing selected item causes a lot of unnecessary API calls.
  let itemId;
  const { tab } = yield select(selectRouteParams);
  const { isMsInventoryVenue } = yield select(selectVenueByVenueId, venueId);

  if (isMsInventoryVenue) {
    yield all([
      call(fetchEntity, practitioners, [client, venueId]),
      call(fetchEntity, resources, [client, venueId]),
      call(fetchEntity, services, [client, venueId]),
      call(fetchEntityIfNeeded, msInventoryVenues, [client, venueId]),
    ]);

    itemId = yield select(selectTabFirstItemId, tab, venueId);
  } else {
    yield call(handleClassesRouteEnter, client, { venueId });
  }

  const newPath = makePath.manageServices({ venueId, tab, itemId });
  yield call(client.navigate, newPath);
}

export function* doHideServiceBanner() {
  const userId = yield select(selectUserId);
  yield call(setKeyForUser, userId, NEW_SERVICE_BANNER_LOCAL_STORAGE_KEY, false);
}

export function* watchActions(client) {
  yield takeEvery(GO_TO_TAB, doGoToTab, client);
  yield takeEvery(GO_TO_ADD, doGoToAdd, client);
  yield takeEvery(GO_TO_EDIT, doGoToEdit, client);
  yield takeEvery(GO_TO_ITEM, doGoToItem, client);
  yield takeEvery(ACTIVE_VENUE_CHANGED, handleActiveVenueChange, client);
  yield takeEvery(HIDE_SERVICE_BANNER, doHideServiceBanner, client);
}
